'use client'
import { Button } from "@/components/ui/Button";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { Gradient } from "@/components/ui/Gradient";
import Image from "next/image";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";

export default function HomePage(): JSX.Element {
  const { theme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Use a default image until hydration is complete
  const brainImageSrc = mounted ? (theme === 'dark' ? "/brain-white.svg" : "/brain-black.svg") : "/brain-black.svg";

  const chaosImageSrc = mounted ? (theme === 'dark' ? "/chaos-to-clarity-white.svg" : "/chaos-to-clarity-black.svg") : "/chaos-to-clarity-black.svg";

  return (
    <main className=" flex min-h-screen flex-col text-black dark:text-white">

      {/* Hero Section */}
      <Gradient variant="hero" className="md:pt-32 pb-20">
        <div className="mx-auto px-4 flex flex-col items-center text-center">
          <h1 className="text-5xl md:text-6xl font-heading tracking-tight mb-8 text-gray-900 dark:text-gray-50">
          The Next Evolution in 
            <span className="text-orange-900 dark:text-eggshell-100 dark:text-transition-colors duration-200"> AI</span>
          </h1>
          <SectionHeader variant='primary' size='md' className="py-4">
            Quantum Cognition Machine Learning (QCML)
          </SectionHeader>

          <p className="text-gray-700 dark:text-gray-300 pb-8 max-w-xl">
            A new form of machine learning based on quantum principles
            inspired by ideas of quantum cognition and implemented on classical computers. 
          </p>
          
          <Button size="lg" href="mailto:contact@qognitive.io">Schedule a Demo</Button>
        </div>
      </Gradient>

      {/* Features Section */}
      <section className="pt-16">
        <div className=" mx-auto px-4">  
          <SectionHeader variant='primary' size='md' className="mb-4 text-center">Available Across Industries</SectionHeader>
          {/* QCML Features */}
          <div className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="p-6 bg-white dark:bg-black rounded-lg shadow-sm">
                <SectionHeader variant='subtle' size='md' className="mb-4">Finance</SectionHeader>
                <p className="text-gray-600 dark:text-gray-300">Financial modeling tools to identify market trends and optimize investment strategies.</p>
                <Button variant="link" size="sm" href="/industry/finance" className="mt-4">Learn More</Button>
              </div>
              <div className="p-6 bg-white dark:bg-black rounded-lg shadow-sm">
                <SectionHeader variant='subtle' size='md' className="mb-4">Medicine</SectionHeader>
                <p className="text-gray-600 dark:text-gray-300">Diagnostic algorithms and research that enhance accuracy and speed in medical diagnosis.</p>
                <Button variant="link" size="sm" href="/industry/medicine" className="mt-4">Learn More</Button>
              </div>
              <div className="p-6 bg-white dark:bg-black rounded-lg shadow-sm">
                <SectionHeader variant='subtle' size='md' className="mb-4">Insurance</SectionHeader>
                <p className="text-gray-600 dark:text-gray-300">Tools for Insurance claims processing and fraud detection.</p>
              </div>
            </div>
          </div>

        </div>
      </section>


      <section className="py-12">
        <div className='flex flex-col md:flex-row justify-center items-center'>
          <div className='mx-12 mb-4 md:mb-0 w-1/4 md:w-full'>
            <Image src={brainImageSrc}
              alt="brain" 
              width={600} 
              height={600}
            />
          </div>
          <div className="mx-auto px-4 md:px-12">
            <SectionHeader variant='primary' size='md' className="py-2">A breakthrough in machine learning</SectionHeader>
            <p className="text-gray-700 dark:text-gray-300">
              Inspired by quantum cognition principles, QCML offers logarithmic economy of representation, making it exceptionally powerful for complex data challenges. This means QCML can handle vastly more complex problems with far fewer resources.
            </p>
          </div>
        </div>
        
      </section>

      <section className="md:py-20 py-12">
        <div className='flex flex-col md:flex-row-reverse justify-center items-center'>

          <div className='mb-0 w-72 md:w-full'>
            <Image src={chaosImageSrc}
              alt="chaos" 
              width={1500} 
              height={890}
            />
          </div>
          
          <div className="mx-auto px-4 md:px-12">
            <SectionHeader variant='primary' size='md' className="py-2">Chaos to Clarity</SectionHeader>
            <p className="text-gray-700 dark:text-gray-300">
            Harnessing quantum manifold principles, our system cuts through data chaos to reveal hidden insights where traditional methods fail. </p>
          </div>

          
        </div>
        
      </section>

      

      <div className="flex justify-center items-center">
            <div className=" w-full max-w-3xl ">
              <div className="relative w-full h-0 pb-[56.25%]">
                <iframe 
                  src="https://player.vimeo.com/video/1047630393#t=1m30s?badge=0&autopause=0&player_id=0&app_id=58479&texttrack=en-x-autogen" 
                  frameBorder="0" 
                  allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media" 
                  style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%'}} 
                  title="IBM Research Distinguished Speaker Series: Kharen Musaelian"
                />
              </div>
            </div>
        </div>

      

      {/* Contact Section */}
      <Gradient variant="contact" className="mt-12 pt-12" id="contact">
        <div className="mx-auto px-4 text-center">
          <SectionHeader variant='primary' size='lg'>Get in Touch</SectionHeader>
          <p className="text-lg mb-8 max-w-2xl mx-auto text-gray-700 dark:text-gray-300">
            Ready to transform your business with AI? 
            <br/>Contact us today to learn how Qognitive can help.
          </p>
          <Button size="lg" href="mailto:contact@qognitive.io">Contact Us</Button>
        </div>
      </Gradient>


      {/* About Section */}
      <section id="about" className="py-20 ">
        <div className=" mx-auto px-4">
          <SectionHeader variant='primary' size='md'>About Qognitive</SectionHeader>
          <div className="pt-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Qognitive is at the forefront of AI innovation, helping businesses leverage artificial intelligence
              to solve complex problems and drive growth.
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Our team of experts combines deep technical knowledge with business acumen to deliver
              solutions that make a real impact on your bottom line.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
